// Phase 34 — At-risk customers dashboard.
// Surfaced from the Reports page tile + accessible at /reports/at-risk-customers.
import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Mail, RefreshCcw, User } from "lucide-react";

const FLAG_LABELS = {
  overdue_invoices: { label: "Overdue invoices", className: "bg-red-100 text-red-700 border-red-200" },
  frequency_dropped: { label: "Frequency drop",  className: "bg-amber-100 text-amber-800 border-amber-200" },
};

export default function AtRiskCustomers() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  // Phase 34 follow-up — manual-reminder confirm dialog state.
  const [confirmRow, setConfirmRow] = useState(null);   // row being confirmed
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/customers/at-risk");
      setRows(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(qq) ||
        (r.company_name || "").toLowerCase().includes(qq) ||
        (r.primary_phone || "").toLowerCase().includes(qq)
    );
  }, [rows, q]);

  const sendReminder = (row) => {
    if (!row.most_recent_overdue_invoice_id) {
      toast.error("No overdue invoice to remind about");
      return;
    }
    setConfirmRow(row);
  };

  const confirmSendReminder = async () => {
    if (!confirmRow) return;
    const row = confirmRow;
    setSending(true);
    try {
      const { data } = await api.post(
        `/invoice-reminders/${row.most_recent_overdue_invoice_id}/send-now`,
        { tier: "manual" },
      );
      const to = (data?.to_emails || []).join(", ") || "customer";
      toast.success(`Reminder sent to ${to}`);
      // Local row update so the page reflects the new timestamp without a refetch.
      setRows((prev) =>
        prev.map((r) =>
          r.customer_id === row.customer_id
            ? { ...r, last_manual_reminder_at: data?.sent_at || new Date().toISOString() }
            : r,
        ),
      );
      setConfirmRow(null);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page space-y-5" data-testid="at-risk-page">
      <div className="flex items-center gap-3">
        <Link
          to="/reports"
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          data-testid="at-risk-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> Reports
        </Link>
      </div>

      <div>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-red-600">// Risk radar</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
          <AlertTriangle className="h-8 w-8 text-red-600" /> At-risk customers
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Customers flagged when invoices are past due OR when their booking
          cadence has fallen behind their historical average. Sorted by composite
          risk score (overdue×2 + frequency drop×1).
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search by name, company or phone…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm h-9"
          data-testid="at-risk-search"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={busy}
          data-testid="at-risk-refresh"
        >
          <RefreshCcw className={"h-3 w-3 mr-1 " + (busy ? "animate-spin" : "")} /> Refresh
        </Button>
        <div className="ml-auto text-sm text-muted-foreground" data-testid="at-risk-count">
          <b className="text-foreground">{filtered.length}</b> at-risk
          {filtered.length !== rows.length && ` (of ${rows.length})`}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div
          className="bg-card border border-dashed border-border rounded-[10px] p-10 text-center"
          data-testid="at-risk-empty"
        >
          <div className="text-3xl mono text-muted-foreground">all clear</div>
          <p className="text-sm text-muted-foreground mt-2">
            {rows.length === 0
              ? "No customers currently meet the at-risk criteria. Nicely done."
              : "No matches for your search."}
          </p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-[10px] overflow-x-auto">
          <table className="w-full text-sm" data-testid="at-risk-table">
            <thead className="bg-secondary/40">
              <tr className="text-left">
                <th className="px-4 py-3 mono text-[10px] uppercase tracking-wider">Customer</th>
                <th className="px-4 py-3 mono text-[10px] uppercase tracking-wider">Flags</th>
                <th className="px-4 py-3 mono text-[10px] uppercase tracking-wider text-right">Overdue</th>
                <th className="px-4 py-3 mono text-[10px] uppercase tracking-wider text-right">Last booking</th>
                <th className="px-4 py-3 mono text-[10px] uppercase tracking-wider text-right">Median gap</th>
                <th className="px-4 py-3 mono text-[10px] uppercase tracking-wider text-right">Risk</th>
                <th className="px-4 py-3 mono text-[10px] uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.customer_id}
                  className="border-t border-border hover:bg-secondary/20"
                  data-testid={`at-risk-row-${r.customer_id}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-bold">{r.company_name || r.name}</div>
                    <div className="text-[12px] text-muted-foreground">
                      {r.name !== r.company_name && r.name}
                      {r.primary_phone && (
                        <span className="ml-2 mono">{r.primary_phone}</span>
                      )}
                    </div>
                    {r.last_manual_reminder_at && (
                      <div
                        className="text-[10px] mono uppercase tracking-wider text-muted-foreground mt-1"
                        data-testid={`at-risk-last-manual-${r.customer_id}`}
                      >
                        Last manual reminder: {r.last_manual_reminder_at.slice(0, 16).replace("T", " ")}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(r.risk_flags || []).map((f) => (
                        <span
                          key={f}
                          className={
                            "text-[10px] mono uppercase tracking-wider px-2 py-0.5 rounded-full border " +
                            (FLAG_LABELS[f]?.className || "bg-secondary text-foreground border-border")
                          }
                          data-testid={`at-risk-flag-${r.customer_id}-${f}`}
                        >
                          {FLAG_LABELS[f]?.label || f}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {r.overdue_invoice_count > 0 ? (
                      <>
                        <div className="font-bold">{formatCurrency(r.overdue_amount_total, "EUR")}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {r.overdue_invoice_count} invoice{r.overdue_invoice_count === 1 ? "" : "s"}
                        </div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {r.last_booking_date ? (
                      <>
                        <div>{r.last_booking_date.slice(0, 10)}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {r.days_since_last_booking}d ago
                        </div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                    {r.median_inter_booking_gap_days != null
                      ? `${r.median_inter_booking_gap_days}d`
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className="inline-flex items-center justify-center min-w-[2.5rem] px-2 py-1 rounded-full bg-red-600 text-white font-black text-sm tabular-nums"
                      data-testid={`at-risk-score-${r.customer_id}`}
                    >
                      {r.risk_score}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {r.risk_flags?.includes("overdue_invoices") && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mr-2"
                        onClick={() => sendReminder(r)}
                        data-testid={`at-risk-send-reminder-${r.customer_id}`}
                      >
                        <Mail className="h-3 w-3 mr-1" /> Send reminder
                      </Button>
                    )}
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => navigate(`/customers/${r.customer_id}`)}
                      data-testid={`at-risk-view-customer-${r.customer_id}`}
                    >
                      <User className="h-3 w-3 mr-1" /> Customer 360
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Phase 34 follow-up — manual reminder confirm dialog */}
      <AlertDialog open={!!confirmRow} onOpenChange={(v) => !v && !sending && setConfirmRow(null)}>
        <AlertDialogContent data-testid="at-risk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send manual reminder</AlertDialogTitle>
            <AlertDialogDescription>
              Send a reminder email for invoice{" "}
              <span className="mono font-semibold text-foreground">
                {confirmRow?.most_recent_overdue_invoice_number || "—"}
              </span>{" "}
              to <span className="mono font-semibold text-foreground">
                {confirmRow?.company_name || confirmRow?.name || "this customer"}
              </span>?
              <br />
              <br />
              This sends the same reminder template as the automatic 3-day reminder
              and is logged on the invoice. It does <b>not</b> stop the scheduled
              3/7/14-day automatic reminders from firing when their windows arrive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending} data-testid="at-risk-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={sending}
              onClick={(e) => { e.preventDefault(); confirmSendReminder(); }}
              data-testid="at-risk-confirm-send"
            >
              {sending ? "Sending…" : "Send now"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
